import { Box } from '@chakra-ui/react';
import { useEffect, useState, useRef } from 'react';

const ProgressiveBg = ({
  backgroundImage,
  display = 'block',
  style = {},
  ...rest
}) => {
  const [loadedSources, setLoadedSources] = useState({});
  const [isInView, setIsInView] = useState(true);
  const containerRef = useRef(null);

  // Intersection Observer for lazy loading with dynamic rootMargin
  useEffect(() => {
    if (!containerRef.current) return;

    // Determine margin based on screen size
    const isMobile = window.matchMedia('(max-width: 768px)').matches;
    const rootMargin = isMobile ? '50% 0px' : '100% 0px'; // half viewport on mobile, full viewport on desktop
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      {
        root: null,
        rootMargin,
        threshold: 0.1,
      }
    );

    observer.observe(containerRef.current);

    return () => observer.disconnect();
  }, []);

  // Load images when component comes into view
  useEffect(() => {
    if (!isInView) return;

    const entries = Object.entries(backgroundImage);
    const loadImages = async () => {
      const promises = entries
        .filter(([_, src]) => src && src !== 'none') // Skip "none" and falsy values
        .map(([key, src]) => {
          const img = new Image();
          img.src = src;
          return new Promise(resolve => {
            img.onload = () => resolve([key, src]);
            img.onerror = () => resolve([key, 'error']); // Optional: mark errors
          });
        });

      const results = await Promise.all(promises);

      // Include skipped "none" values in the final result
      const skipped = entries
        .filter(([_, src]) => !src || src === 'none')
        .map(([key, src]) => [key, src]);

      const loadedMap = Object.fromEntries([...results, ...skipped]);

      setLoadedSources(loadedMap);
    };

    loadImages();
  }, [backgroundImage, isInView]);

  return (
    <div ref={containerRef} className='progressive-bg'>
      {Object.entries(backgroundImage).map(([bp, src]) => {
        const displayObj = {
          base: 'none',
          sm: 'none',
          md: 'none',
          lg: 'none',
          xl: 'none',
          '2xl': 'none',
          [bp]: display,
        };

        const blurSrc = src === 'none' ? src : `${src}?blur=40`;
        const loadedSrc = loadedSources[bp];
        const isNotInView = !isInView;

        return (
          <Box
            key={bp}
            display={displayObj}
            {...rest}
            backgroundImage={
              src === 'none'
                ? src
                : isNotInView
                ? blurSrc
                : `url(${loadedSrc || blurSrc})`
            }
            style={{
              ...style,
              filter: isNotInView
                ? 'blur(10px)'
                : loadedSrc
                ? 'blur(0px)'
                : 'blur(10px)',
              transition: 'filter 0.5s ease',
            }}
          />
        );
      })}
    </div>
  );
};

export default ProgressiveBg;
